import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAsync, useCountdown, useLottoSocket } from "../lib";
import { Countdown, Empty, Skeleton, FlagBadge } from "../ui";

function Digits({ value, tone }) {
  const text = String(value ?? "").trim();
  if (!text) return <span className="lx-yk-none">-</span>;
  return (
    <span className="lx-yk-digits">
      {text.split("").map((d, i) => (
        <i key={i} className={`lx-yk-digit ${tone}`}>
          {d}
        </i>
      ))}
    </span>
  );
}

function YeekeeRow({ round }) {
  const nav = useNavigate();
  const clock = useCountdown(round.close_at);
  return (
    <div className="lx-yk-card open">
      <div className="lx-yk-head">
        <FlagBadge code={round.type_code} category={round.category} size={44} />
        <div className="lx-yk-title">
          <b>{round.type_name || round.type_code}</b>
          <span>{round.round_label}</span>
        </div>
        <span className={`lx-pill ${clock.done ? "closed" : "open"}`}>
          {clock.done ? "ปิดรับ" : "เปิดรับ"}
        </span>
      </div>
      <div className="lx-yk-clock">
        <span className="lx-yk-clock-label">ปิดรับแทงใน</span>
        <Countdown clock={clock} />
      </div>
      <button
        className="lx-btn gradient block"
        disabled={clock.done}
        onClick={() => nav(`/yeekee/${round.id}`)}
      >
        <Icon icon="solar:bolt-bold" width={16} height={16} /> เข้าห้องแทง
      </button>
    </div>
  );
}

function ResultCard({ r }) {
  const top3 = String(r.top3 ?? "").trim();
  const top2 = String(r.top2 ?? "").trim() || (top3.length >= 2 ? top3.slice(-2) : "");
  return (
    <div className="lx-yk-card">
      <div className="lx-yk-head">
        <div className="lx-yk-title">
          <b>{r.round_label}</b>
          <span>{r.type_name || "ยี่กี"}</span>
        </div>
        <Icon icon="solar:cup-star-bold-duotone" width={22} height={22} style={{ color: "var(--lx-gold)" }} />
      </div>
      <div className="lx-yk-main">
        <span className="lx-yk-tag top">
          <Icon icon="solar:arrow-up-bold" width={12} height={12} /> เลขบน · 3 ตัว
        </span>
        <Digits value={top3} tone="top" />
      </div>
      <div className="lx-yk-sub">
        <div className="lx-yk-cell">
          <span className="lx-yk-tag top">
            <Icon icon="solar:arrow-up-bold" width={12} height={12} /> 2 ตัวบน
          </span>
          <Digits value={top2} tone="top" />
        </div>
        <div className="lx-yk-cell">
          <span className="lx-yk-tag bottom">
            <Icon icon="solar:arrow-down-bold" width={12} height={12} /> 2 ตัวล่าง
          </span>
          <Digits value={r.bottom2} tone="bottom" />
        </div>
      </div>
    </div>
  );
}

export default function YeekeePage() {
  const { data: rounds, loading } = useAsync(() => lottoApi.openRounds(), []);
  const { data: seed } = useAsync(() => lottoApi.results({ limit: 40 }), []);
  const [feed, setFeed] = useState([]);

  const yeekeeRounds = useMemo(
    () => (rounds || []).filter((r) => String(r.type_code || "").startsWith("YEEKEE")),
    [rounds],
  );

  useEffect(() => {
    if (seed) setFeed(seed.filter((r) => String(r.type_code || "").startsWith("YEEKEE")).slice(0, 20));
  }, [seed]);

  useLottoSocket(null, {
    onRoundSettled: () => {
      lottoApi
        .results({ limit: 20 })
        .then((rs) => setFeed(rs.filter((r) => String(r.type_code || "").startsWith("YEEKEE"))))
        .catch(() => {});
    },
  });

  return (
    <div style={{ marginTop: 18 }}>
      <div
        className="lx-card vx-pagehead"
        style={{
          marginBottom: 16,
      }}
      >
        <div className="lx-h1">
          ห้องยี่กีสด
          <Icon icon="solar:bolt-bold" width={20} height={20} style={{ verticalAlign: -4, marginLeft: 6, color: "var(--lx-accent-2)" }} />
        </div>
        <p className="lx-muted" style={{ margin: "6px 0 0" }}>
          ออกผลอัตโนมัติทุก 15 นาที · ตรวจสอบย้อนหลังได้ (provably-fair)
        </p>
      </div>

      <h2 className="lx-h2" style={{ marginBottom: 10 }}>
        รอบที่กำลังเปิด
      </h2>
      {loading && <Skeleton h={90} />}
      {!loading && !yeekeeRounds.length && (
        <div className="lx-card">
          <Empty icon="solar:hourglass-line-bold-duotone">ยังไม่มีรอบเปิด</Empty>
        </div>
      )}
      <div className="lx-yk-grid">
        {yeekeeRounds.map((r) => (
          <YeekeeRow key={r.id} round={r} />
        ))}
      </div>

      <h2 className="lx-h2" style={{ margin: "22px 0 10px" }}>
        ผลล่าสุด
      </h2>
      {!feed.length ? (
        <div className="lx-card">
          <Empty icon="solar:ticket-bold-duotone">ยังไม่มีผล</Empty>
        </div>
      ) : (
        <div className="lx-yk-grid">
          {feed.map((r) => (
            <ResultCard key={r.round_id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}
